import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { NgForm } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { isNil } from 'lodash';
import { CommonModule } from '@angular/common';
import { REMOVED, TestSetupModule } from '@nrwl/test-setup';
import { CommonMaterialModule } from '@nrwl/common-material';
import {
  CommonRegEx,
  SchedulerFormat,
  SchedulerComponent,
  CommonLibModule,
  DevicesService,
  LicensesPageService
} from '@nrwl/common-lib';
import { BertSetupService } from '../../bert-setup.service';
import {
  BaseSetupTab,
  IComponentStateHistory,
  IValueChangedEvent,
  outputParams
} from '@nrwl/test-setup';
import {
  LAYER_TYPES,
  BertConfigFieldMapping as ConfigFieldMapping,
  CONFIRM_SELECTION,
  BERT_PATTERN_SELECTION,
  PERF_TEST_DURATIONS
} from '../../models/model';
import { ScheduleLicense } from '@nrwl/licenses';
import { cloneDeep } from 'lodash';
@Component({
  selector: 'nrwl-bert-setup',
  standalone: true,
  imports: [CommonModule, TestSetupModule, CommonMaterialModule, FormsModule, CommonLibModule],
  templateUrl: './bert-setup.component.html',
  styleUrls: ['./bert-setup.component.scss']
})
export class BertSetupComponent implements OnInit, OnDestroy {
  @Input() tab: BaseSetupTab;
  @Input() setupCtrl: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() componentId: string;
  @Input() stateHistory?: IComponentStateHistory;
  @Input() otherTabStateHistory: IComponentStateHistory;
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() restoreValues?: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() restoreAgentBasedConfig?: Function;
  @Input() previousOutput: outputParams;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onOtherTabDataValueChange = new EventEmitter<any>();
  @ViewChild('setupForm', { static: true }) form: NgForm;
  @ViewChild(SchedulerComponent) schedulerChild: SchedulerComponent;
  configMapping = ConfigFieldMapping;
  newTestMode = true;
  customerName = '';
  contact = '';
  email = '';
  phone = '';
  technicianId = '';
  comments='';
  technicianName = '';
  testName = 'bert';
  editMode = false;
  cloneMode = false;
  testInfo: any;
  config: any;
  outputs: Array<string>;
  validTestDurationRegex: RegExp = CommonRegEx.HH_MM_SS_REGEX;
  addingColonTwoCharRegex: RegExp = CommonRegEx.COLON_TWO_CHARACTORS;
  layerTypes = LAYER_TYPES;
  swapMacAddressesTypes = CONFIRM_SELECTION;
  ipAddressTypes = [];
  bertPatternTypes = BERT_PATTERN_SELECTION;
  customDuration = 900;
  setupFormField = {
    layerToTest: null,
    ipAddrType: null,
    bertPattern: null,
    swapMacAddresses: true,
    perfTestDuration: null,
    customBertTestDuration: '00:15:00'
  };
  tabsubscription: Subscription;
  agentType = null;
  scheduler: SchedulerFormat;
  scheduleInfoData: SchedulerFormat = {
    repeatInterval: 0,
    timeStamp: '',
    endTime: '',
    isValid: true
  };
  isDisabledScheduler = false;
  schedulerEndsDurations = PERF_TEST_DURATIONS;
  schedulerEnds = {
    enabled: true,
    optionsList: PERF_TEST_DURATIONS
  };
  readonly unsubscribe: Subject<void> = new Subject();
  bertScheduleLicense = ScheduleLicense.BERT;
  isvalidCustomDuration = true;
  customDurationErrorMsg = null;
  formTooltip = {
    ipAddressType: 'Layer 3 testing is limited to IPV4.'
  };
  streams = [];
  minDurationSeconds = 15;

  constructor(
    private readonly licenseService: LicensesPageService,
    public setupService: BertSetupService,
    private readonly deviceService: DevicesService
  ) {}

  ngOnInit(): void {
    this.tab.form = this.form;
    this.schedulerEnds.optionsList = this.schedulerEndsDurations;
    this.initializeData();
    if (this.setupService.tabDataChangeEvents) {
      this.tabsubscription = this.setupService.tabDataChangeEvents.subscribe(event => {
        if (event === 0) {
          this.restoreFormValues();
        }
      });
    }
    this.licenseService.activeTestLicenseDetails.subscribe(licenseData => {
      if (licenseData?.length) {
        this.isDisabledScheduler = !licenseData.some(license =>
          this.bertScheduleLicense?.includes(license.featureName)
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.complete();
    this.unsubscribe.next();
    if (this.tabsubscription) {
      this.tabsubscription.unsubscribe();
    }
  }

  updateTestIdentity(event: {
    testName: string;
    customer: string;
    technician: string;
    isValidName: boolean;
    contact: string;
    email: string;
    phone: string;
    technicianId: string;
    comments: string;
  }): void {
    this.onDataEmit('alias', event.testName, event.isValidName);
    this.onDataEmit('customer', event.customer, true);
    this.onDataEmit('technician', event.technician, true);
    this.onDataEmit('contact', event.contact, true);
    this.onDataEmit('email', event.email, true);
    this.onDataEmit('phone', event.phone, true);
    this.onDataEmit('technicianId', event.technicianId, true);
    this.onDataEmit('comments',event.comments,true);
    const controls = this.form?.form?.controls;
    const allValid = controls ? Object.values(controls).every(control => !control.invalid) : true;
    const isFormValid = event.isValidName && allValid;
    this.form.form?.setErrors(isFormValid ? null : { invalid: true });
  }

  initializeData(): void {
    this.restoreAgentBasedConfig();
    this.restoreFormValues();
    this.restoreOtherTabValues();
    this.newTestMode = this.setupService.mode === 'new';
    this.editMode = this.setupService.editMode;
    this.cloneMode = this.setupService.mode === 'clone';
  }

  restoreFormValues(): void {
    this.tab.form = this.form;
    this.config = this.setupService.currentTab.config;
    this.outputs = this.config.def.outputs;
    this.ipAddressTypes = this.config.options[ConfigFieldMapping.IP_ADDRESS_TYPE].value;
    this.testName = this.restoreFieldValues(ConfigFieldMapping.ALIAS);
    this.customerName = this.restoreFieldValues(ConfigFieldMapping.CUSTOMER);
    this.technicianName = this.restoreFieldValues(ConfigFieldMapping.TECHINICIAN);
    this.contact = this.restoreFieldValues(ConfigFieldMapping.CONTACT);
    this.email = this.restoreFieldValues(ConfigFieldMapping.EMAIL);
    this.phone = this.restoreFieldValues(ConfigFieldMapping.PHONE);
    this.technicianId = this.restoreFieldValues(ConfigFieldMapping.TECHINICIANID);
    this.comments = this.restoreFieldValues(ConfigFieldMapping.COMMENTS);
    this.agentType = this.restoreFieldValues(ConfigFieldMapping.AGENT_TYPE) || 'default';
    this.layerTypes = LAYER_TYPES;
    this.setupFormField.layerToTest = this.restoreFieldValues(ConfigFieldMapping.LAYER_TO_TEST);
    this.setupFormField.bertPattern = this.restoreFieldValues(ConfigFieldMapping.BERT_PATTERN);
    this.setupFormField.ipAddrType = this.restoreFieldValues(ConfigFieldMapping.IP_ADDRESS_TYPE);
    this.setupFormField.perfTestDuration = this.restoreFieldValues(
      ConfigFieldMapping.BERT_TEST_DURATION
    );
    this.setupFormField.customBertTestDuration = this.restoreFieldValues(
      ConfigFieldMapping.BERT_TEST_CUSTOM_DURATION
    );
    this.restoreScheduleParams(ConfigFieldMapping.SCHEDULER);
  }

  restoreFieldValues(field: string): any {
    const data = this.stateHistory(this.componentId, field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }

  showFormField(field: string): boolean {
    return this.outputs.includes(field);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  onDataEmit(key: string, newValue: any, isValid: boolean): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      key: key,
      newValue: newValue,
      isValid: isValid
    });
  }

  onLayerChange(): void {
    this.onDataEmit(ConfigFieldMapping.LAYER_TO_TEST, this.setupFormField.layerToTest, true);
  }

  onIpAddressTypeChange(): void {
    this.onDataEmit(ConfigFieldMapping.IP_ADDRESS_TYPE, this.setupFormField.ipAddrType, true);
  }

  onBertPatternChange(): void {
    this.onDataEmit(ConfigFieldMapping.BERT_PATTERN, this.setupFormField.bertPattern, true);
    this.streams[0].name = 'BERT ' + this.setupFormField.bertPattern;
    this.onOtherTabDataValueChange.emit({
      componentId: 'bert.streams.html',
      key: ConfigFieldMapping.STREAMS,
      newValue: this.streams,
      isValid: true
    });
  }

  //eslint-disable-next-line
  restoreAdvancedFieldValues(field: string): any {
    const data = this.otherTabStateHistory('bert.streams.html', field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }

  restoreOtherTabValues(): void {
    this.streams = cloneDeep(this.restoreAdvancedFieldValues(ConfigFieldMapping.STREAMS));
  }

  restoreScheduleParams(field: string): void {
    const data = this.stateHistory(this.componentId, field);
    if (data && data.value !== REMOVED) {
      this.populateScheduler(data.value);
    }
  }

  populateScheduler(schedulerInfo: SchedulerFormat): void {
    this.scheduleInfoData.repeatInterval = schedulerInfo.repeatInterval;
    this.scheduleInfoData.timeStamp = schedulerInfo.timeStamp;
    //this.scheduleInfoData.endTime = schedulerInfo.endTime;
    this.scheduleInfoData.endTime = this.setupFormField.perfTestDuration;
    this.scheduleInfoData.isValid = schedulerInfo.isValid;
    this.emitScedulerParams();
  }

  //** get scheduler param values from scheduler component */
  fetchSchedulerVal(event: SchedulerFormat): void {
    this.scheduler = event;
    this.scheduleInfoData.repeatInterval = event.repeatInterval;
    this.scheduleInfoData.timeStamp = this.setupService.getScheduleTimeStamp(event.timeStamp);
    this.scheduleInfoData.isValid = event.isValid;
    this.scheduleInfoData.endTime = event.endTime;
    if (this.scheduleInfoData.endTime) {
      this.onDataEmit(ConfigFieldMapping.BERT_TEST_DURATION, this.scheduleInfoData.endTime, true);
    }
    this.emitScedulerParams();
  }

  emitScedulerParams(): void {
    this.onDataEmit('scheduler', this.scheduleInfoData, this.scheduleInfoData.isValid);
  }
}
